import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FRONTDESK_LANGUAGE_CATALOG } from "../../../lib/i18n/language-catalog";
import { useAccessibilitySession } from "../accessibility-session";
import { LanguageTiles } from "./LanguageTiles";
import { LocalEmergencyTile } from "./LocalEmergencyTile";
import { KioskStep } from "../../../components/kiosk-step";
import { StepLoading } from "./StepStatus";

const ALL_LANGUAGE_CODES = FRONTDESK_LANGUAGE_CATALOG.map(({ code }) => code);

/** Language-first entry shares the existing session text/audio language owner.
 * Every catalog autonym is offered as a touch-sized tile; one tap starts the session.
 * @since 1.1.8
 * @example <LanguageEntry />
 */
export function LanguageEntry({ children }: { readonly children?: ReactNode }) {
  const { t } = useTranslation();
  const { beginPresentationSession, sessionLanguage } = useAccessibilitySession();
  return (
    <KioskStep
      width="wide"
      align="center"
      title={t("self-service.language-entry.title")}
      titleTestId="frontdesk-language-entry-title"
    >
      <LanguageTiles
        codes={ALL_LANGUAGE_CODES}
        testIdPrefix="frontdesk-language-entry"
        selectedCode={sessionLanguage}
        onSelect={beginPresentationSession}
      />
      {children}
    </KioskStep>
  );
}
/** Compose authorized offline emergency access with language-first entry.
 * @since 1.1.8
 * @example <LanguageEntryWithLocalEmergency onEmergency={() => undefined} />
 */
export function LanguageEntryWithLocalEmergency({ onEmergency }: { readonly onEmergency: () => void }) {
  return <LanguageEntry><LocalEmergencyTile onSelect={onEmergency} /></LanguageEntry>;
}
/** Neutral entry state while terminal settings decide between language entry and the menu.
 * Authorized local emergency access stays available while settings load.
 * @since 1.1.8
 * @example <SessionEntryLoading>{emergencyTile}</SessionEntryLoading>
 */
export function SessionEntryLoading({ children }: { readonly children?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="flex h-full min-h-0 flex-col gap-5 px-8 py-6">
      <div className="min-h-0 flex-1"><StepLoading message={t("self-service.loading")} /></div>
      {children}
    </div>
  );
}
